import React from "react";

import _ from "lodash";
import { connect } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { KeywordGeneratorToolPageModal } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageModal";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { keywordGroupsTransforms } from "app/services/keyword-groups/utils/keywordGroupsTransforms";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TableSelection } from "pro-features/components/TableSelection/src/TableSelection";
import {
    TableSelectionGroupKeywordComponent,
    generalGroupToastMessage,
} from "pro-features/components/TableSelection/src/TableSelectionGroupKeywordComponent";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import TrackProvider from "pro-features/components/WithTrack/src/TrackProvider";
import TranslationProvider from "pro-features/components/WithTranslation/src/TranslationProvider";
import { showSuccessToast } from "../../actions/toast_actions";
import { getToastItemComponent } from "../../components/React/Toast/ToastItem";
import { i18nFilter } from "../../filters/i18nFilter";
import { allTrackers } from "../../services/track/track";

import type { ITitleValidation } from "./KeywordGroupEditorHelpers";
import { KeywordGroupEditorHelpers } from "./KeywordGroupEditorHelpers";

const KEYWORD_ANALYSIS_STATE = "organicsearch_keywordAnalysis_2";

interface IAddTableRowsKeywordsToGroupUtilityProps {
    onViewGroupClick?: (keywordGroupId: string) => void;
    getToastLink?: (keywordGroupId: string) => string;
    [key: string]: any;
}

class AddTableRowsKeywordsToGroupUtility extends React.PureComponent<
    IAddTableRowsKeywordsToGroupUtilityProps,
    {
        showNewGroup: boolean;
        isLoading: boolean;
        mode: ETableSelectionNewGroupDropdownMode;
        currentGroup?: string;
        isCreateNewListModalOpen: boolean;
        isModalOpen: boolean;
        newlyCreatedGroup: boolean;
        groupName: string;
        workspaceId: string;
        keywordGroups: IUserKeywordGroup[];
        isKeywordGroupsLoading: boolean;
    } & ITitleValidation
> {
    private dropdownRef: any;
    private maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount();
    private i18n: ReturnType<typeof i18nFilter>;
    private keywordGroupState: any;

    constructor(props: IAddTableRowsKeywordsToGroupUtilityProps) {
        super(props);
        this.i18n = i18nFilter();
        this.state = {
            showNewGroup: false,
            isLoading: false,
            isValid: true,
            errorMessage: undefined,
            mode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            isCreateNewListModalOpen: false,
            isModalOpen: false,
            groupName: "",
            workspaceId: "",
            newlyCreatedGroup: false,
            keywordGroups: [],
            isKeywordGroupsLoading: true,
        };
        this.keywordGroupState =
            this.props.keywordGroupState ??
            (this.props.isSearchTrends
                ? "digitalsuite_marketresearch_keywordmarketanalysis_total"
                : "organicsearch_keywordAnalysis_overview_2");
    }

    public async componentDidMount() {
        await this.loadKeywordGroups();
    }

    private loadKeywordGroups = async () => {
        try {
            this.setState({ isKeywordGroupsLoading: true });
            const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
            this.setState({
                keywordGroups: [...userGroups, ...sharedGroups],
                isKeywordGroupsLoading: false,
            });
        } catch (error) {
            Sentry.error("Error loading keyword groups", error as Error);
            this.setState({
                keywordGroups: [],
                isKeywordGroupsLoading: false,
            });
        }
    };

    public render() {
        const key = `table.selection.keywords${this.props.selectedRows.length > 1 ? ".multiple" : ""}`;
        const selectedText = this.i18n(key, { count: this.props.selectedRows.length.toString() });
        return (
            <>
                <TranslationProvider translate={this.i18n}>
                    <TrackProvider track={allTrackers.trackEvent.bind(allTrackers)}>
                        <TableSelection
                            isVisible={true}
                            groupSelectorElement={this.getDropdown()}
                            addToGroupLabel={this.i18n("table.selection.keywords.addto")}
                            onCloseClick={this.onCloseClick}
                            selectedText={selectedText}
                            tooltipText={this.i18n("table.selection.clear.tooltip")}
                            showSeparator={false}
                            isDisabled={this.props.selectedRows?.length === 0}
                        />
                    </TrackProvider>
                </TranslationProvider>
                <KeywordGeneratorToolPageModal
                    isOpen={this.state.isModalOpen}
                    onCloseClick={this.onModalClose}
                    onViewGroupClick={this.onViewGroupClick}
                    onStartOverClick={this.onStartOverClick}
                    newlyCreatedGroup={this.state.newlyCreatedGroup}
                />
            </>
        );
    }

    private onModalClose = () => {
        this.setState({ isModalOpen: false });
    };

    private onViewGroupClick = () => {
        if (this.props.onViewGroupClick) {
            this.props.onViewGroupClick(this.state.groupName);
        } else {
            const { country, duration } = proNavigator.getParams();
            const { groupName } = this.state;
            proNavigator.go(this.keywordGroupState, {
                country,
                duration,
                keyword: `*${groupName}`,
                isTerm: false,
            });
        }
    };

    private onStartOverClick = () => {
        this.props.clearAllSelectedRows();
        this.setState({ isModalOpen: false });
    };

    private setRef = (ref) => {
        this.dropdownRef = ref;
    };

    private getDropdown = () => {
        return (
            <TableSelectionGroupKeywordComponent
                ref={this.setRef}
                groups={keywordGroupsTransforms.toDropDown(this.state.keywordGroups)}
                onSubmit={this.onSubmit}
                isLoading={this.state.isLoading}
                error={!this.state.isValid}
                errorMessage={this.state.errorMessage}
                onGroupClick={this.onGroupClick}
                onNewListButtonClick={this.onNewListButtonClick}
                mode={this.state.mode}
                isCreateNewListModalOpen={this.state.isCreateNewListModalOpen}
                onCancel={this.onCancel}
                groupIconName="keyword-group"
                count={this.maxKeywordsInGroup}
                newGroupNameLabel={this.i18n("table.selection.newgroup.keywords.title")}
                trackingCategory="add to keyword group"
                allItemsExistsMessage="table.selection.keywords.exists"
                maxItemsMessage="table.selection.keywords.max.keywords"
                alertMessage={
                    this.isRowLimitExceeded()
                        ? this.i18n("table.selection.newgroup.keywords.alert.message", {
                              MaxKWs: this.maxKeywordsInGroup,
                          })
                        : undefined
                }
                selectedRows={this.props.selectedRows}
            />
        );
    };

    protected onNewListButtonClick = () => {
        this.setState({ isCreateNewListModalOpen: true });
    };

    private onCloseClick = () => {
        allTrackers.trackEvent("add to keyword group", "close", "toolbar");
        this.props.clearAllSelectedRows();
    };

    private onCancel = () => {
        if (this.state.mode === ETableSelectionNewGroupDropdownMode.NEW_GROUP) {
            allTrackers.trackEvent("add to keyword group", "click", "Create New Keyword Group/Cancel");
        }
        this.setState({
            mode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            isValid: true,
            errorMessage: undefined,
            isCreateNewListModalOpen: false,
        });
    };

    private onSubmit = async (name) => {
        const validation = await KeywordGroupEditorHelpers.validateTitle(name);
        if (!validation.isValid) {
            this.setState({
                ...validation,
            });
        } else {
            await this.toggleLoading();
            const selectedRows = this.isRowLimitExceeded()
                ? this.props.selectedRows.slice(0, this.maxKeywordsInGroup)
                : this.props.selectedRows;
            const group = KeywordGroupEditorHelpers.keywordGroupFromList({
                title: name,
                items: selectedRows.map((keyword) => ({ text: keyword })),
            });
            const createdGroup = group.Id
                ? await keywordGroupsQueryClient.updateGroup(group)
                : await keywordGroupsQueryClient.createGroup(group);
            if (createdGroup) {
                this.setState({
                    groupName: createdGroup.Id,
                });
                await this.toggleLoading();
                this.props.clearAllSelectedRows();
                this.setState({
                    mode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
                    isValid: true,
                    errorMessage: undefined,
                    isCreateNewListModalOpen: false,
                    isModalOpen: true,
                    newlyCreatedGroup: true,
                });
                await this.loadKeywordGroups();
            } else {
                this.dropdownRef.close();
            }
        }
    };

    private isRowLimitExceeded = () => {
        return this.props.selectedRows.length > this.maxKeywordsInGroup;
    };

    private onGroupClick = async (item) => {
        if (!item.id) {
            this.onNewGroupClick(item);
        } else {
            this.onExistingGroupClick(item);
        }
    };

    private onNewGroupClick = (item) => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New Keyword List/Save/${item.name || ""}/${this.props.selectedRows.length.toString() || ""}`,
        );
        if (this.props.selectedRows.length > KeywordGroupEditorHelpers.getMaxGroupCount()) {
            this.setState({
                mode: ETableSelectionNewGroupDropdownMode.MAX_ITEMS,
            });
            this.dropdownRef.openWarning();
        } else {
            this.setState({
                mode: ETableSelectionNewGroupDropdownMode.NEW_GROUP,
            });
        }
    };

    private onExistingGroupClick = async (item) => {
        const currentGroup = await keywordGroupsQueryClient.getGroupById(item.id);
        const currentKeywords = currentGroup.Keywords.map((keyword) => {
            return { text: keyword };
        });
        const newKeywords = this.props.selectedRows.map((keyword) => {
            return { text: keyword };
        });
        const withoutDuplications = _.uniqWith(
            [...currentKeywords, ...newKeywords],
            (crr, other) => crr.text === other.text,
        );
        const newKeywordsCount = withoutDuplications.length - currentKeywords.length;
        if (newKeywordsCount === 0) {
            this.setStateAsync({
                mode: ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS,
                currentGroup: currentGroup.Name,
            });
            this.dropdownRef.openWarning();
        } else {
            const group = KeywordGroupEditorHelpers.keywordGroupFromList(
                {
                    title: currentGroup.Name,
                    items: withoutDuplications,
                },
                currentGroup,
            );
            if (group.Keywords.length > this.maxKeywordsInGroup) {
                this.setState({
                    mode: ETableSelectionNewGroupDropdownMode.MAX_ITEMS,
                });
                this.dropdownRef.openWarning();
            } else {
                const updatedGroup = group.Id
                    ? await keywordGroupsQueryClient.updateGroup(group)
                    : await keywordGroupsQueryClient.createGroup(group);
                if (updatedGroup) {
                    this.showGroupLinkToast(
                        currentGroup.Id,
                        generalGroupToastMessage(currentGroup.Name, newKeywordsCount),
                        this.i18n("table.selection.keywords.seegroup"),
                        KEYWORD_ANALYSIS_STATE,
                    );
                    this.props.clearAllSelectedRows();
                    this.setState({
                        mode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
                        isValid: true,
                        errorMessage: undefined,
                    });
                    await this.loadKeywordGroups();
                }
            }
        }
    };

    private toggleLoading = () => {
        return this.setStateAsync({
            isLoading: !this.state.isLoading,
        });
    };

    private showGroupLinkToast = (name, text, label, stateLink?: string) => {
        const { country, duration } = proNavigator.getParams();
        let linkToGroup = proNavigator.href(stateLink ?? this.keywordGroupState, {
            country,
            duration,
            keyword: `*${name}`,
        });

        if (this.props.getToastLink) {
            linkToGroup = this.props.getToastLink(name);
        }

        this.dropdownRef.close();
        this.props.showToast(linkToGroup, text, label);
    };

    private setStateAsync = (state) => {
        return new Promise<void>((resolve) => {
            this.setState(state, resolve);
        });
    };
}

const mapStateToProps = (state, ownProps) => {
    const { stateKey, propertyKey = "SearchTerm" } = ownProps;
    return {
        selectedRows: getTableSelectedItems(stateKey)(state).map((it: any) => it[propertyKey]),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    const { clearAllSelectedRows } = ownProps;
    return {
        clearAllSelectedRows: () => {
            dispatch(clearAllSelectedRows());
        },
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: allTrackers.trackEvent.bind(
                            allTrackers,
                            "add to keyword group",
                            "click",
                            "internal link/keywordAnalysis.overview",
                        ),
                    }),
                ),
            );
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddTableRowsKeywordsToGroupUtility);
